import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress, Typography } from '@mui/material';
import NavbarC from './components/NavbarC';
import FooterC from './components/FooterC';
import ScrollToTop from './components/ScrollToTop';
import Products from './pages/Products';
import Plugins from './pages/Plugins';
import PluginDetail from './pages/PluginDetail';
import Login from './pages/Login';
import MyProducts from './pages/MyProducts';
import Computers from './pages/Computers';
import Downloads from './pages/Downloads';
import Profile from './pages/Profile';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ProtectedCheckout from './components/ProtectedCheckout';
import NewsletterConfirm from './pages/NewsletterConfirm';
import News from './pages/News';
import NewsDetail from './pages/NewsDetail';
import About from './pages/About';
import Contacts from './pages/Contacts';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import PurchaseSuccess from './pages/PurchaseSuccess';
import AlreadyOwned from './pages/AlreadyOwned';
import ConfirmPurchaseEmail from './pages/ConfirmPurchaseEmail';
import VerifyEmail from './pages/VerifyEmail';
import CheckoutPay from './pages/CheckoutPay';
import CheckoutCancel from './pages/CheckoutCancel';
import CookiePolicy from './pages/CookiePolicy';
import ResetPasswordC from './components/ResetPasswordC';
import AdminPanelC from './components/AdminPanelC';
import { cleanupOnLoad } from './utils/clearLegacyStorage';
import { LanguageProvider, useLanguageContext } from './contexts/LanguageContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieBanner from './components/CookieBanner';
import CookieSettingsModal from './components/CookieSettingsModal';
import AnalyticsProvider from './components/AnalyticsProvider';
import { initAnalyticsTests } from './utils/analyticsTest';
import UnderConstruction from './components/UnderConstruction';
import { UserProvider } from './contexts/UserContext';
import { useDevMode } from './hooks/useDevMode';
import { useCookieConsent } from './contexts/CookieConsentContext';
import axios from 'axios';
import { clearRedirectUrl, consumeRedirectUrl, destinationAfterLogin } from './utils/redirectHelpers';
import { notifyAuthChanged } from './hooks/useOwnedProductIds';

interface AppContentProps {
  isAuthenticated: boolean;
  onLogout: () => void;
  onLoginSuccess: (token: string) => void;
}

function AppContent({ isAuthenticated, onLogout, onLoginSuccess }: AppContentProps) {
  const { isLoading } = useLanguageContext();
  const { showSettingsModal, closeSettings } = useCookieConsent();
  useDevMode(); // Auto-add dev parameter to URLs

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black z-50"></div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex flex-col overflow-x-hidden">
      <ScrollToTop />
      <NavbarC isAuthenticated={isAuthenticated} onLogout={onLogout} />
      <main className="flex-grow">
        <Routes>
          <Route path="/" element={<Products />} />
          <Route path="/products" element={<Products />} />
          <Route path="/plugins" element={<Plugins />} />
          <Route path="/plugins/:slug" element={<PluginDetail />} />
          <Route path="/checkout/:slug" element={<ProtectedCheckout isAuthenticated={isAuthenticated} />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<NewsDetail />} />
          <Route path="/cart" element={
            <div className="container mx-auto px-4 py-8 text-center">
              <h2 className="text-2xl font-bold mb-4 text-white">Your Cart</h2>
              <p className="text-gray-400">Your cart is empty</p>
            </div>
          } />
          <Route path="/login" element={!isAuthenticated ? <Login onLoginSuccess={onLoginSuccess} /> : <Navigate to="/" />} />
          <Route path="/profile" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
          <Route path="/account/products" element={isAuthenticated ? <MyProducts /> : <Navigate to="/login" />} />
          <Route path="/account/computers" element={isAuthenticated ? <Computers /> : <Navigate to="/login" />} />
          <Route path="/account/downloads" element={<Downloads />} />
          <Route path="/register" element={!isAuthenticated ? <Register onLoginSuccess={onLoginSuccess} /> : <Navigate to="/" />} />
          <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/" />} />
          <Route path="/newsletter/confirm/:token" element={<NewsletterConfirm />} />
          <Route path="/reset-password/:token" element={!isAuthenticated ? <ResetPasswordC /> : <Navigate to="/" />} />
          <Route path="/admin" element={isAuthenticated ? <AdminPanelC /> : <Navigate to="/login" />} />
          <Route path="/about" element={<About />} />
          <Route path="/contacts" element={<Contacts />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/purchase/success" element={<PurchaseSuccess />} />
          <Route path="/purchase/already-owned" element={<AlreadyOwned />} />
          <Route path="/purchase/confirm-email" element={<ConfirmPurchaseEmail />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/checkout/pay" element={<CheckoutPay />} />
          <Route path="/purchase/cancel" element={<CheckoutCancel />} />
          <Route path="/cookies" element={<CookiePolicy />} />
        </Routes>
      </main>
      <FooterC />
      <CookieBanner />
      <CookieSettingsModal isOpen={showSettingsModal} onClose={closeSettings} />
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [gateEnabled, setGateEnabled] = useState(true);
  
  // Check and persist development mode
  const checkDevelopmentMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const devFromUrl = urlParams.get('dev') === 'true';
    
    // Check localStorage for persistent dev mode (expires after 24 hours)
    const devModeData = localStorage.getItem('dev-mode-data');
    let devFromStorage = false;
    
    if (devModeData) {
      try {
        const parsed = JSON.parse(devModeData);
        const now = Date.now();
        const expiresAt = parsed.expiresAt || 0;
        
        // Check if dev mode hasn't expired (24 hours)
        if (now < expiresAt) {
          devFromStorage = parsed.enabled === true;
        } else {
          // Clean up expired dev mode
          localStorage.removeItem('dev-mode-data');
        }
      } catch (e) {
        // Clean up corrupted data
        localStorage.removeItem('dev-mode-data');
      }
    }
    
    // If dev=true in URL, save to localStorage with 24h expiry
    if (devFromUrl) {
      const devModeData = {
        enabled: true,
        expiresAt: Date.now() + (24 * 60 * 60 * 1000) // 24 hours
      };
      localStorage.setItem('dev-mode-data', JSON.stringify(devModeData));
      return true;
    }
    
    // Otherwise check localStorage
    return devFromStorage;
  };
  
  const isDevelopmentMode = checkDevelopmentMode();

  // Add development mode utilities to window for debugging
  useEffect(() => {
    if (isDevelopmentMode) {
      (window as any).devMode = {
        disable: () => {
          localStorage.removeItem('dev-mode-data');
          window.location.href = window.location.origin;
        },
        extend: () => {
          const devModeData = {
            enabled: true,
            expiresAt: Date.now() + (24 * 60 * 60 * 1000) // Extend for another 24 hours
          };
          localStorage.setItem('dev-mode-data', JSON.stringify(devModeData));
          console.log('Dev mode extended for 24 hours');
        },
        status: () => {
          const devModeData = localStorage.getItem('dev-mode-data');
          if (devModeData) {
            try {
              const parsed = JSON.parse(devModeData);
              const now = Date.now();
              const expiresAt = parsed.expiresAt || 0;
              const hoursLeft = Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60 * 60)));
              console.log(`Dev mode: ${parsed.enabled ? 'enabled' : 'disabled'}, expires in ${hoursLeft} hours`);
            } catch (e) {
              console.log('Dev mode: corrupted data');
            }
          } else {
            console.log('Dev mode: disabled');
          }
        }
      };
      
      // Log initial status
      (window as any).devMode.status();
    }
  }, [isDevelopmentMode]);


  // Проверить валидность токена
  const checkAuth = async (token: string) => {
    try {
      await axios.get('/api/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return true;
    } catch (err) {
      return false;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      // Очищаем устаревшие данные согласно правилам приватности
      cleanupOnLoad();
      
      // Initialize analytics testing tools in development (silent)
      if (process.env.NODE_ENV === 'development') {
        initAnalyticsTests();
      }
      
      // Fetch maintenance gate status from backend
      try {
        const { data } = await axios.get('/api/status');
        const gate = data?.maintenance === true;
        setGateEnabled(gate);
      } catch {
        setGateEnabled(true);
      }
      
      const token = localStorage.getItem('token');
      if (token) {
        const isValid = await checkAuth(token);
        setIsAuthenticated(isValid);
        if (!isValid) {
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };
    initAuth();
  }, []);

  const handleLogin = (token: string) => {
    localStorage.setItem('token', token);
    const redirectUrl = consumeRedirectUrl();
    if (redirectUrl) {
      destinationAfterLogin(token, redirectUrl)
        .then((destination) => window.location.assign(destination))
        .catch(() => window.location.assign(redirectUrl));
      return;
    }
    setIsAuthenticated(true);
    notifyAuthChanged();
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    clearRedirectUrl();
    setIsAuthenticated(false);
    notifyAuthChanged();
  };

  // Show under construction only when backend gate is enabled and devMode is not active
  if (gateEnabled && !isDevelopmentMode) {
    return <UnderConstruction />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} sx={{ color: '#a855f7' }} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            Loading...
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <LanguageProvider>
      <CookieConsentProvider>
        <AnalyticsProvider>
          <UserProvider isAuthenticated={isAuthenticated}>
            <AppContent 
              isAuthenticated={isAuthenticated} 
              onLogout={handleLogout} 
              onLoginSuccess={handleLogin}
            />
          </UserProvider>
        </AnalyticsProvider>
      </CookieConsentProvider>
    </LanguageProvider>
  );
}

export default App; 